import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "react-hot-toast";
import api from "../utils/base_url";
import { useAuth } from "../context/AuthContext";
import "../styles/Verification.css";

const Verification = () => {
  const [otp, setOtp] = useState(new Array(6).fill(""));
  const [loading, setLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(60);
  const inputsRef = useRef([]);
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  const { type } = location.state || {};
  const sessionId = sessionStorage.getItem("session_id");

  useEffect(() => {
    if (!type || !sessionId) {
      toast.error("Session expired, please register again.");
      navigate("/register");
    }
  }, [type, sessionId, navigate]);

  useEffect(() => {
    if (resendTimer > 0) {
      const t = setTimeout(() => setResendTimer(v => v - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [resendTimer]);

  const handleChange = (value, index) => {
    if (!/^\d?$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < 5) inputsRef.current[index + 1]?.focus();
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (!pasted) return;
    const newOtp = [...otp];
    pasted.split("").forEach((char, i) => { newOtp[i] = char; });
    setOtp(newOtp);
    const nextEmpty = Math.min(pasted.length, 5);
    inputsRef.current[nextEmpty]?.focus();
  };

  const getApiUrl = () => {
    if (type === "email") return "register/email-step2/";
    if (type === "phone") return "phone-register-step2/";
    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post(getApiUrl(), {
        session_id: sessionId,
        otp: otp.join(""),
      });

      if (res.data?.access && res.data?.refresh) {
        sessionStorage.setItem("access", res.data.access);
        sessionStorage.setItem("refresh", res.data.refresh);
        sessionStorage.setItem("user", JSON.stringify(res.data.user));
        login({ access: res.data.access, refresh: res.data.refresh, user: res.data.user });
        toast.success("OTP verified! Welcome 🌿");
        navigate("/");
      } else {
        toast.error(res.data.message || "Invalid OTP, please try again.");
      }
    } catch (err) {
      const d = err.response?.data;
      toast.error(
        d?.error || d?.errors?.non_field_errors?.[0] || d?.message || "OTP verification failed."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    try {
      setResendTimer(60);
      const endpoint = type === "email" ? "register/email-step1/" : "phone-login-step1/";
      const payload = type === "email"
        ? { email: sessionStorage.getItem("email"), password: sessionStorage.getItem("password") }
        : { phone: sessionStorage.getItem("phone") };

      if (!payload.email && !payload.phone) {
        toast.error("Missing credentials. Please login again.");
        navigate("/login");
        return;
      }

      const res = await api.post(endpoint, payload);
      sessionStorage.setItem("session_id", res.data.session_id);
      toast.success(res.data.message || "OTP resent successfully");
      setOtp(new Array(6).fill(""));
      inputsRef.current[0]?.focus();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to resend OTP");
    }
  };

  const isComplete = otp.every(d => d !== "");
  const channel = type === "email" ? "email address" : "WhatsApp number";

  return (
    <div className="vf-wrapper">
      <motion.div
        className="vf-card"
        initial={{ y: 40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.55, ease: [0.4, 0, 0.2, 1] }}
      >
        {/* Icon */}
        <div className="vf-icon-wrap">
          {type === "email" ? "✉️" : "📱"}
        </div>

        <p className="vf-title">Verify your {type === "email" ? "email" : "phone"}</p>
        <p className="vf-subtitle">
          We sent a 6-digit code to your <strong>{channel}</strong>.
          Enter it below to continue.
        </p>

        <form onSubmit={handleSubmit} noValidate>
          {/* OTP boxes */}
          <div className="vf-otp-row" onPaste={handlePaste}>
            {otp.map((digit, i) => (
              <input
                key={i}
                ref={el => (inputsRef.current[i] = el)}
                className={`vf-otp-input${digit ? " filled" : ""}`}
                type="text"
                inputMode="numeric"
                pattern="\d*"
                maxLength={1}
                value={digit}
                autoComplete={i === 0 ? "one-time-code" : "off"}
                onChange={e => handleChange(e.target.value, i)}
                onKeyDown={e => handleKeyDown(e, i)}
              />
            ))}
          </div>

          <button
            type="submit"
            className="vf-btn"
            disabled={loading || !isComplete}
          >
            {loading && <span className="vf-spinner" aria-hidden />}
            {loading ? "Verifying…" : "Verify OTP"}
          </button>
        </form>

        <hr className="vf-divider" />

        {/* Resend row */}
        <div className="vf-resend-row">
          <span>Didn't receive it?</span>
          {resendTimer > 0 ? (
            <span className="vf-timer">⏱ {resendTimer}s</span>
          ) : (
            <button className="vf-resend-btn" onClick={handleResendOtp}>
              Resend OTP
            </button>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default Verification;